/**
 * Contexte des préférences du Dashboard.
 * Permet d'activer/désactiver les widgets et mémorise les préférences (localStorage).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_DASHBOARD_PREFERENCES,
  type DashboardPreferences,
  type DashboardWidgetId,
  type SortableWidgetId,
} from '@/types/dashboard';
import {
  DASHBOARD_PREFERENCES_STORAGE_KEY,
  loadDashboardPreferences,
  saveDashboardPreferences,
  updateWidgetOrder,
  updateWidgetVisibility,
} from '@/services/dashboardPreferencesService';

interface DashboardPreferencesContextValue {
  /** Préférences actuelles */
  preferences: DashboardPreferences;
  /** Vérifie si un widget est activé */
  isWidgetEnabled: (widgetId: DashboardWidgetId) => boolean;
  /** Active ou désactive un widget */
  setWidgetEnabled: (widgetId: DashboardWidgetId, enabled: boolean) => void;
  /** Ordre des widgets (colonne principale) */
  widgetOrder: SortableWidgetId[];
  /** Met à jour l'ordre des widgets */
  setWidgetOrder: (order: SortableWidgetId[]) => void;
  /** Réinitialise aux valeurs par défaut */
  resetToDefaults: () => void;
}

const DashboardPreferencesContext = createContext<DashboardPreferencesContextValue | null>(null);

export function DashboardPreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<DashboardPreferences>(loadDashboardPreferences);

  const isWidgetEnabled = useCallback(
    (widgetId: DashboardWidgetId): boolean => {
      return preferences.widgets[widgetId] ?? true;
    },
    [preferences.widgets]
  );

  const setWidgetEnabled = useCallback((widgetId: DashboardWidgetId, enabled: boolean) => {
    setPreferences(prev => {
      const next = updateWidgetVisibility(prev, widgetId, enabled);
      saveDashboardPreferences(next);
      return next;
    });
  }, []);

  const setWidgetOrder = useCallback((order: SortableWidgetId[]) => {
    setPreferences(prev => {
      const next = updateWidgetOrder(prev, order);
      saveDashboardPreferences(next);
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    const defaults = {
      ...DEFAULT_DASHBOARD_PREFERENCES,
      updatedAt: new Date().toISOString(),
    };
    setPreferences(defaults);
    saveDashboardPreferences(defaults);
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DASHBOARD_PREFERENCES_STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as DashboardPreferences;
          setPreferences(parsed);
        } catch {
          // ignore
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const value = useMemo<DashboardPreferencesContextValue>(
    () => ({
      preferences,
      isWidgetEnabled,
      setWidgetEnabled,
      widgetOrder: preferences.widgetOrder,
      setWidgetOrder,
      resetToDefaults,
    }),
    [preferences, isWidgetEnabled, setWidgetEnabled, setWidgetOrder, resetToDefaults]
  );

  return (
    <DashboardPreferencesContext.Provider value={value}>
      {children}
    </DashboardPreferencesContext.Provider>
  );
}

export function useDashboardPreferences(): DashboardPreferencesContextValue {
  const ctx = useContext(DashboardPreferencesContext);
  if (!ctx) {
    throw new Error('useDashboardPreferences must be used within DashboardPreferencesProvider');
  }
  return ctx;
}
